import type {
  GridApiPremium,
  GridColDef,
  GridGroupingColDefOverride,
  GridRowClassNameParams,
} from "@mui/x-data-grid-premium";
import { GRID_CHECKBOX_SELECTION_COL_DEF, useGridApiRef } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useMemo } from "react";
import type { ApiError } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import DataGridGroupHeader from "@entrilia/shared/components/grid/DataGridGroupHeader";
import { getSelectedRowIdsFromModel } from "@entrilia/shared/components/grid/gridHelper.ts";
import {
  checkSelection,
  getRowNode,
  setAllNonGroupRowsSelection,
} from "@entrilia/shared/utilities/dataGridApiHelper.ts";
import type { OrganizationReportInfo } from "../../../../../api/types/svTypes.ts";
import { getCheckedGridSx } from "../../../../common/grid/gridStyles";
import useGridExpand from "../../../reporting/report-lists/reports/hooks/useGridExpand";
import type { GridKey } from "../../../reporting/report-lists/storage/reportsGridExpandStorage";
import GroupCell from "./GroupCell";

interface Props {
  reports: OrganizationReportInfo[];
  loading: boolean;
  error: ApiError | undefined;
  getTreeDataPath: ((row: OrganizationReportInfo) => readonly string[]) | undefined;
  Toolbar: React.ReactNode;
  selectedIds: string[];
  currentReport: OrganizationReportInfo | undefined;
  gridKey: GridKey;
  setSelectedIds: (ids: string[]) => void;
  getColumns: (apiRef: RefObject<GridApiPremium | null>) => GridColDef[];
  getViewReportUrl: (clientCode: string, reportId: string) => string;
}

const getRowId = (row: OrganizationReportInfo) => row.reportId;

const OrganizationReportsGridWrapper = ({
  reports,
  error,
  loading,
  getColumns,
  getTreeDataPath,
  Toolbar,
  selectedIds,
  setSelectedIds,
  currentReport,
  getViewReportUrl,
  gridKey,
}: Props) => {
  const apiRef = useGridApiRef();
  const showToolbar = useMemo(() => selectedIds.length > 0, [selectedIds]);
  const fullSelection = checkSelection(apiRef).checked;
  const { isGroupExpandedByDefault, toggleSingleGridRow, toggleAllGridRows } = useGridExpand({ gridKey, apiRef });

  const groupingColDef = useMemo(
    (): GridGroupingColDefOverride => ({
      sortable: true,
      headerName: "Companies",
      width: 500,
      renderCell: (params) => (
        <GroupCell params={params} getViewReportUrl={getViewReportUrl} onExpandToggle={toggleSingleGridRow} />
      ),
      renderHeader: (params) => <DataGridGroupHeader params={params} onExpandToggle={toggleAllGridRows} />,
    }),
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [toggleSingleGridRow, toggleAllGridRows]
  );

  const columns: GridColDef[] = useMemo(() => getColumns(apiRef), [apiRef, getColumns]);

  const getRowClassName = useCallback(
    (params: GridRowClassNameParams<OrganizationReportInfo>) => {
      if (getRowNode(apiRef, params.id)?.type === "group") {
        return "";
      }
      if (params.row.reportId === currentReport?.reportId) {
        return "Mui-hovered";
      }
      return "";
    },
    [apiRef, currentReport]
  );

  if (error) {
    return <DataLoadingFailed text={error.message || "Failed to load reports"} />;
  }

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={showToolbar}
      style={{ paddingLeft: 6 }}
      fullSelection={fullSelection}
      selection={[selectedIds, () => setAllNonGroupRowsSelection(apiRef, false)]}
      actions={Toolbar}
    >
      <DataGrid<OrganizationReportInfo>
        apiRef={apiRef}
        treeData
        getTreeDataPath={getTreeDataPath}
        columns={columns}
        rows={reports}
        getRowId={getRowId}
        sx={getCheckedGridSx}
        defaultGroupingExpansionDepth={2}
        loading={loading}
        checkboxSelection
        groupingColDef={groupingColDef}
        disableColumnReorder
        //***  The workaround related to the issue https://github.com/mui/mui-x/issues/18642
        initialState={{
          columns: {
            orderedFields: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
          },
        }}
        disableColumnPinning
        //***
        slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
        getRowClassName={getRowClassName}
        onRowSelectionModelChange={(newSelection) => setSelectedIds(getSelectedRowIdsFromModel(newSelection))}
        sortingOrder={["asc", "desc"]}
        shiftActionsLeft
        isGroupExpandedByDefault={isGroupExpandedByDefault}
      />
    </DataGridHeaderToolbarProvider>
  );
};

export default OrganizationReportsGridWrapper;
